import React from 'react';
import { Container, Grid, Typography, Box, Paper, Button, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ViewListIcon from '@mui/icons-material/ViewList';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const TILE_HEIGHT = 180;
const TILE_MARGIN = 6;

interface SetupTileProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const SetupTile: React.FC<SetupTileProps> = ({ title, description, icon, onClick }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Box sx={{ mb: TILE_MARGIN }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          height: TILE_HEIGHT,
          display: 'flex', 
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          bgcolor: 'grey.50',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
            bgcolor: 'grey.100'
          }
        }}
      >
        <Box sx={{ height: 40, mb: 1 }}>
          <Typography 
            variant="subtitle1" 
            sx={{
              fontWeight: 'bold',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.1,
              fontSize: '0.95rem'
            }}
          >
            {title}
          </Typography>
        </Box>
        
        <Box sx={{ height: 52, mb: 1.5 }}>
          <Typography 
            variant="body2" 
            sx={{
              fontSize: '0.8rem',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.3
            }}
          >
            {description}
          </Typography>
        </Box>
        
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ color: 'primary.main', fontSize: 20 }}>
            {icon}
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={onClick}
            size="small"
            sx={{ py: 0.5, px: 2 }}
          >
            Configure
          </Button>
        </Box>
      </Paper>
    </Box>
  </Grid>
);

const OrganizationSetup: React.FC = () => {
  const navigate = useNavigate();
  
  const setupTiles = [
    {
      title: 'Review Definitions',
      description: 'Browse and manage review definitions for projects, programs, and accounts',
      icon: <ViewListIcon />,
      path: '/review-definitions'
    },
    {
      title: 'Questionnaire Management',
      description: 'Manage questionnaire templates',
      icon: <UploadFileIcon />,
      path: '/questionnaire-management'
    },
    {
      title: 'Review Entity Attributes',
      description: 'Add/manage project or program or unit attributes',
      icon: <CategoryIcon />,
      path: '/entity-attributes'
    },
    {
      title: 'Set up Review Entity Types',
      description: 'Configure the different types of entities that can be reviewed in your organization',
      icon: <CategoryIcon />,
      path: '/organization-setup/entity-types'
    },
    {
      title: 'Set up Metric Levels',
      description: 'Define metric grains such as Sprint, Release, Milestone, Asset Group, and Team',
      icon: <TuneIcon />,
      path: '/organization-setup/metric-levels'
    },
    {
      title: 'Set up Review Dimensions',
      description: 'Define the various dimensions by which entities can be reviewed',
      icon: <DashboardCustomizeIcon />,
      path: '/organization-setup/dimensions'
    },
    // Hidden for now — may be needed in future
    // {
    //   title: 'Set up Review Frequency',
    //   description: 'Configure how often different types of reviews should occur',
    //   icon: <CalendarMonthIcon />,
    //   path: '/organization-setup/frequency'
    // },
    {
      title: 'Set up Metric Thresholds',
      description: 'Configure organization-level metric thresholds for different review dimensions',
      icon: <TuneIcon />,
      path: '/organization-setup/metric-thresholds'
    },
    {
      title: 'LLM Prompt Configuration',
      description: 'Configure AI-powered review summary prompts for different review types and entity types',
      icon: <SmartToyIcon />,
      path: '/organization-setup/llm-prompt-config'
    }
  ];

  return (
    <Box>
  <TitleBar />
  <Container sx={{ mt: 4, mb: 6 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <IconButton 
        onClick={() => navigate('/home')} 
        aria-label="Back"
        sx={{
          border: '1px solid #ccc',
          borderRadius: 2,
          p: 1,
          backgroundColor: '#f5f5f5',
          '&:hover': {
            backgroundColor: '#e0e0e0',
          },
          mr: 2
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <Typography variant="h4" gutterBottom>
        Organization Defaults
      </Typography>
    </Box>

        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Configure organization-wide settings and defaults for the Statusense platform
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={4}>
            {setupTiles.map((tile, index) => (
              <SetupTile
                key={index}
                title={tile.title}
                description={tile.description}
                icon={tile.icon}
                onClick={() => navigate(tile.path)}
              />
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default OrganizationSetup;
